import { CORE_API_URL } from '../api/api';
import { clearUserCache } from './cache';

// The Firebase SDK is loaded on demand (it's only needed inside the async
// session/token flows below) so it stays out of the main bundle.
let firebasePromise = null;
const loadFirebaseAuth = () => {
    if (!firebasePromise) {
        firebasePromise = Promise.all([
            import('../firebase/config'),
            import('firebase/auth'),
        ]).then(([config, firebaseAuth]) => ({
            auth: config.auth,
            signInWithCustomToken: firebaseAuth.signInWithCustomToken,
        })).catch((error) => {
            firebasePromise = null;
            throw error;
        });
    }
    return firebasePromise;
};

const AUTH_STATE_CHANGE_EVENT = 'authStateChange';
export const SESSION_EXPIRED_EVENT = 'sessionExpired';

// Firebase tokens expire after 1 hour — used to schedule proactive refreshes
// so the applicant backend's Bearer-token auth never sees a stale token.
const TOKEN_EXPIRY_DURATION = 3600 * 1000;

/**
 * Cached Firebase ID token (used by the applicant backend's
 * firebase_login_required decorator via Authorization: Bearer header,
 * and by CityAutocomplete via the auth-token header for the marketplace API).
 */
export const getAuthToken = () => {
    return localStorage.getItem('auth_token');
};

export const getEmail = () => {
    return localStorage.getItem('email');
};

export const getUserType = () => {
    return localStorage.getItem('user_type');
};

export const getUserId = () => {
    return localStorage.getItem('user_id');
};

/**
 * Store user context from the session-based login response.
 * The real auth credential is the httponly session cookie set by core-api —
 * localStorage only holds display/context data.
 */
export const setAuthData = (email, userType, userId) => {
    if (email) localStorage.setItem('email', email);
    if (userType) localStorage.setItem('user_type', userType);
    if (userId) localStorage.setItem('user_id', userId);
    window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
};

/**
 * Cache a Firebase ID token locally so synchronous callers
 * (getAuthHeaders, getAuthToken) can access it without awaiting.
 */
export const cacheFirebaseToken = (token) => {
    localStorage.setItem('auth_token', token);
    const expiryTime = Date.now() + TOKEN_EXPIRY_DURATION;
    localStorage.setItem('token_expiry_time', expiryTime.toString());
};

export const clearAuthData = () => {
    clearUserCache();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('email');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_id');
    localStorage.removeItem('token_expiry_time');
    window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
};

/**
 * Quick client-side check — real session validity is enforced server-side.
 */
export const isAuthenticated = () => {
    return !!getEmail();
};

/**
 * Authorization header for the applicant backend (firebase_login_required).
 */
export const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * True when the cached Firebase token is expired or about to expire (5-min buffer).
 * Used by AuthProvider to schedule proactive refreshes.
 */
export const isTokenExpired = () => {
    const expiryTime = localStorage.getItem('token_expiry_time');
    if (!expiryTime) return true;
    const bufferTime = 5 * 60 * 1000;
    return Date.now() >= (parseInt(expiryTime, 10) - bufferTime);
};

/**
 * Sign into the Firebase SDK using the session cookie.
 * Calls core-api /auth/firebase-token which validates the session
 * and returns a Firebase custom token, then signs in the SDK and
 * caches the resulting ID token.
 */
export const signInWithSessionCookie = async () => {
    try {
        const { auth, signInWithCustomToken } = await loadFirebaseAuth();
        const response = await fetch(`${CORE_API_URL}/auth/firebase-token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (auth && data.firebase_token) {
            await signInWithCustomToken(auth, data.firebase_token);
            const idToken = await auth.currentUser.getIdToken();
            cacheFirebaseToken(idToken);
            return true;
        }
        return false;
    } catch {
        return false;
    }
};

/**
 * Refresh the Firebase ID token from the SDK and update the cache.
 * If the SDK has no current user, attempts to restore it via the
 * session cookie first.
 */
export const refreshFirebaseToken = async () => {
    try {
        const { auth } = await loadFirebaseAuth();
        let currentUser = auth.currentUser;

        if (!currentUser) {
            const recovered = await signInWithSessionCookie();
            if (!recovered) return null;
            currentUser = auth.currentUser;
        }

        const newToken = await currentUser.getIdToken(true);
        cacheFirebaseToken(newToken);
        return newToken;
    } catch (error) {
        if (error.code === 'auth/user-token-expired' ||
            error.code === 'auth/invalid-user-token' ||
            error.code === 'auth/user-not-found') {
            clearAuthData();
        }
        return null;
    }
};

/**
 * Validate the user session on page load by calling /auth/me.
 * Hydrates localStorage from the session cookie (enabling the
 * isAuthenticated() check), then ensures the Firebase SDK is
 * signed in so that token-based calls to the applicant backend
 * continue to work.
 */
export const validateSessionOnLoad = async () => {
    try {
        const response = await fetch(`${CORE_API_URL}/auth/me`, {
            method: 'GET',
            credentials: 'include',
        });

        if (!response.ok) {
            clearAuthData();
            return false;
        }

        const data = await response.json();

        // Portal no longer gates on user_type; any valid session is accepted.
        setAuthData(data.email, data.user_type, data.user_id);

        const { auth } = await loadFirebaseAuth();

        // Ensure the Firebase SDK is signed in for token auth fallback
        if (!auth?.currentUser) {
            await new Promise((resolve) => {
                if (!auth) { resolve(); return; }
                const unsubscribe = auth.onAuthStateChanged(() => {
                    unsubscribe();
                    resolve();
                });
                setTimeout(() => { unsubscribe(); resolve(); }, 3000);
            });

            if (!auth?.currentUser) {
                await signInWithSessionCookie();
            }
        }

        // Cache a fresh Firebase token for applicant backend calls
        if (auth?.currentUser) {
            const token = await auth.currentUser.getIdToken();
            cacheFirebaseToken(token);
        }

        return true;
    } catch {
        clearAuthData();
        return false;
    }
};

// Alias so existing AuthProvider import keeps working
export const validateTokenOnLoad = validateSessionOnLoad;
